import {
  type ComponentType,
  type ReactNode,
} from 'react';

import { cn, hasRenderableNode } from '../base/utils';
import {
  getDefaultGalleryDisplayText,
  getDefaultGalleryFieldValue,
  getDefaultGalleryImagePreviewSrc,
  isDefaultGalleryAudioField,
  isDefaultGalleryImageField,
  isDefaultGalleryMediaColumn,
  isDefaultGallerySummaryCandidate,
  isDefaultGalleryTitleCandidate,
  renderDefaultGalleryFieldValue,
  type WorkbenchGalleryColumn,
} from './workbenchGalleryUtils';
import {
  getWorkbenchIconComponent,
  renderWorkbenchIcon,
} from './workbenchIconUtils';

export interface WorkbenchGalleryRecordVisual {
  Icon?: ComponentType<{ className?: string }> | ReactNode;
  icon?: ComponentType<{ className?: string }> | ReactNode;
  chipClassName?: string;
  iconClassName?: string;
  label?: ReactNode;
  title?: ReactNode;
  [key: string]: any;
}

export interface WorkbenchGalleryLabels {
  fieldCount?: (count: number) => ReactNode;
  coverOnlyHint?: ReactNode;
}

export interface WorkbenchGalleryCardProps<Row = any> {
  row: Row;
  columns: WorkbenchGalleryColumn[];
  onClick?: () => void;
  cardInteraction?: 'button' | 'container';
  isSelected?: boolean;
  isMasonry?: boolean;
  galleryFieldKey?: string;
  galleryDensity?: 'compact' | 'comfortable' | 'detailed' | string;
  galleryBentoColumns?: 1 | 2 | 3 | number;
  galleryDetailVisibility?: 'all' | 'limit' | string;
  galleryDetailLimit?: number | string;
  galleryDetailPresentation?: 'tiles' | 'lines';
  shouldEnhanceMultimodal?: boolean;
  getFieldValue?: (column: WorkbenchGalleryColumn, row: Row) => unknown;
  isMediaColumn?: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  isTitleCandidate?: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  isSummaryCandidate?: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  getDisplayText?: (value: unknown) => ReactNode;
  renderFieldValue?: (
    column: WorkbenchGalleryColumn,
    row: Row,
    context: { compact?: boolean; surface?: string },
  ) => ReactNode;
  getImagePreviewSrc?: (value: unknown, rowId?: string | number, columnLabel?: ReactNode) => string;
  isImageField?: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
  isAudioField?: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
  AudioPlayerComponent?: ComponentType<{ filename: string }>;
  cardVariant?: 'default' | 'image-only' | 'preview-only' | 'chromatic-bento' | string;
  renderPreview?: (context: WorkbenchGalleryCardRenderContext<Row>) => ReactNode;
  renderMeta?: (context: WorkbenchGalleryCardMetaContext<Row>) => ReactNode;
  renderLeadingVisual?: (context: WorkbenchGalleryCardMetaContext<Row> & { iconVisual?: WorkbenchGalleryRecordVisual | null }) => ReactNode;
  resolveRecordVisual?: (value: unknown, row: Row, iconColumn?: WorkbenchGalleryColumn) => WorkbenchGalleryRecordVisual | null | undefined;
  hiddenDetailFieldKeys?: string[];
  labels?: WorkbenchGalleryLabels;
  formatLabel?: (value: ReactNode, context?: { column?: WorkbenchGalleryColumn }) => ReactNode;
  className?: string;
}

export interface WorkbenchGalleryCardRenderContext<Row = any> {
  row: Row;
  mediaColumn?: WorkbenchGalleryColumn | null;
  mediaValue: unknown;
  isImageMedia: boolean;
  isAudioMedia: boolean;
  iconVisual?: WorkbenchGalleryRecordVisual | null;
  IconComponent?: ComponentType<{ className?: string }> | null;
  previewClassName: string;
}

export interface WorkbenchGalleryCardMetaContext<Row = any> {
  row: Row;
  contentColumns: WorkbenchGalleryColumn[];
  detailColumns: WorkbenchGalleryColumn[];
  titleColumn?: WorkbenchGalleryColumn | null;
  summaryColumn?: WorkbenchGalleryColumn | null;
  getDisplayText: (value: unknown) => ReactNode;
  getFieldValue: (column: WorkbenchGalleryColumn, row: Row) => unknown;
}

const getDefaultGalleryDetailLimit = (density?: string) => {
  if (density === 'compact') return 2;
  if (density === 'detailed') return 6;
  return 4;
};

const getNormalizedGalleryBentoColumns = (columns: unknown, density?: string) => {
  if (columns === 1 || columns === 2 || columns === 3) return Number(columns);
  return density === 'detailed' ? 1 : 2;
};

const getNormalizedGalleryDetailLimit = ({
  density,
  visibility,
  limit,
}: {
  density?: string;
  visibility?: string;
  limit?: number | string;
}) => {
  if (visibility === 'all') return Number.POSITIVE_INFINITY;
  const parsedLimit = Number.parseInt(String(limit), 10);
  if (Number.isFinite(parsedLimit) && parsedLimit > 0) return parsedLimit;
  return getDefaultGalleryDetailLimit(density);
};

const getGalleryCardPaddingClassName = (density?: string) => {
  if (density === 'compact') return 'p-3';
  if (density === 'detailed') return 'p-4';
  return 'p-3.5';
};

const getGalleryPreviewClassName = (density?: string, isMasonry?: boolean) => {
  if (isMasonry) return 'h-auto w-full object-cover';
  if (density === 'compact') return 'h-32 w-full object-cover';
  if (density === 'detailed') return 'h-48 w-full object-cover';
  return 'h-40 w-full object-cover';
};

const getGalleryDetailGridClassName = (columns: number) => {
  if (columns === 1) return 'grid-cols-1';
  if (columns === 3) return 'grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3';
  return 'grid-cols-1 sm:grid-cols-2';
};

function getIconComponent(icon: WorkbenchGalleryRecordVisual['Icon']) {
  return getWorkbenchIconComponent(icon) as ComponentType<{ className?: string }> | null;
}

function defaultFormatLabel(value: ReactNode) {
  return value;
}

function getLabelText(value: ReactNode) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '';
}

export function WorkbenchGalleryCard<Row = any>({
  row,
  columns,
  onClick,
  cardInteraction = 'button',
  isSelected = false,
  isMasonry = false,
  galleryFieldKey = 'auto',
  galleryDensity = 'comfortable',
  galleryBentoColumns,
  galleryDetailVisibility = 'limit',
  galleryDetailLimit,
  galleryDetailPresentation = 'tiles',
  shouldEnhanceMultimodal = true,
  getFieldValue,
  isMediaColumn,
  isTitleCandidate,
  isSummaryCandidate,
  getDisplayText,
  renderFieldValue,
  getImagePreviewSrc,
  isImageField,
  isAudioField,
  AudioPlayerComponent,
  cardVariant = 'default',
  renderPreview,
  renderMeta,
  renderLeadingVisual,
  resolveRecordVisual,
  hiddenDetailFieldKeys = [],
  labels,
  formatLabel = defaultFormatLabel,
  className,
}: WorkbenchGalleryCardProps<Row>) {
  const resolvedGetFieldValue = getFieldValue || getDefaultGalleryFieldValue;
  const resolvedGetDisplayText = getDisplayText || getDefaultGalleryDisplayText;
  const resolvedIsImageField = isImageField || isDefaultGalleryImageField;
  const resolvedIsAudioField = isAudioField || isDefaultGalleryAudioField;
  const resolvedGetImagePreviewSrc = getImagePreviewSrc || getDefaultGalleryImagePreviewSrc;
  const resolvedIsMediaColumn = isMediaColumn || ((column: WorkbenchGalleryColumn, candidateRow: Row) => (
    isDefaultGalleryMediaColumn(column, candidateRow, {
      getFieldValue: resolvedGetFieldValue as any,
      shouldEnhanceMultimodal,
      isImageField: resolvedIsImageField,
      isAudioField: resolvedIsAudioField,
    })
  ));
  const resolvedIsTitleCandidate = isTitleCandidate || ((column: WorkbenchGalleryColumn, candidateRow: Row) => (
    isDefaultGalleryTitleCandidate(column, candidateRow, { isMediaColumn: resolvedIsMediaColumn as any })
  ));
  const resolvedIsSummaryCandidate = isSummaryCandidate || ((column: WorkbenchGalleryColumn, candidateRow: Row) => (
    isDefaultGallerySummaryCandidate(column, candidateRow, { isMediaColumn: resolvedIsMediaColumn as any })
  ));
  const resolvedRenderFieldValue = renderFieldValue || ((column: WorkbenchGalleryColumn, candidateRow: Row, { compact } = {}) => (
    renderDefaultGalleryFieldValue(column, candidateRow, {
      compact,
      getFieldValue: resolvedGetFieldValue as any,
      getDisplayText: resolvedGetDisplayText,
    })
  ));
  const isPreviewOnly = cardVariant === 'preview-only';
  const isImageOnly = cardVariant === 'image-only';
  const isChromaticBento = cardVariant === 'chromatic-bento';
  const selectedGalleryColumn = galleryFieldKey !== 'auto'
    ? columns.find((column) => column.key === galleryFieldKey && resolvedIsMediaColumn(column, row))
    : null;
  const mediaColumn = selectedGalleryColumn || (shouldEnhanceMultimodal ? columns.find((column) => resolvedIsMediaColumn(column, row)) : null);
  const iconColumn = columns.find((column) => column.renderType === 'icon');
  const iconVisual = iconColumn && resolveRecordVisual ? resolveRecordVisual((row as any)[iconColumn.key], row, iconColumn) : null;
  const IconComponent = getIconComponent(iconVisual?.Icon || iconVisual?.icon);
  const mediaValue = mediaColumn ? resolvedGetFieldValue(mediaColumn, row) : null;
  const isImageMedia = mediaColumn ? resolvedIsImageField(mediaColumn, mediaValue) : false;
  const isAudioMedia = mediaColumn ? resolvedIsAudioField(mediaColumn, mediaValue) : false;
  const contentColumns = columns.filter((column) => column.key !== mediaColumn?.key && column.renderType !== 'icon');
  const titleCandidates = contentColumns.filter((column) => {
    if (!column || resolvedIsMediaColumn(column, row) || !resolvedIsTitleCandidate(column, row)) return false;
    const value = resolvedGetFieldValue(column, row);
    return !(value === null || value === undefined || value === '');
  });
  const titleColumn = titleCandidates[0] || null;
  const titleText = titleColumn ? resolvedGetDisplayText(resolvedGetFieldValue(titleColumn, row)) : resolvedGetDisplayText((row as any).linkKey || (row as any).id);
  const summaryCandidates = contentColumns.filter(column => column && column.key !== titleColumn?.key && !resolvedIsMediaColumn(column, row) && resolvedIsSummaryCandidate(column, row));
  const summaries = summaryCandidates.map(column => ({ column, text: String(resolvedGetDisplayText(resolvedGetFieldValue(column, row))) }));
  const summaryColumn = (summaries.find(({ text }) => text !== '—' && text.length > 12)
    || summaries.find(({ text }) => text !== '—'))?.column || null;
  const summaryText = summaryColumn ? resolvedGetDisplayText(resolvedGetFieldValue(summaryColumn, row)) : '';
  const hiddenDetailKeySet = new Set(hiddenDetailFieldKeys);
  const detailColumns = contentColumns
    .filter((column) => column.key !== titleColumn?.key && column.key !== summaryColumn?.key && !hiddenDetailKeySet.has(column.key));
  const resolvedDetailLimit = getNormalizedGalleryDetailLimit({
    density: galleryDensity,
    visibility: galleryDetailVisibility,
    limit: galleryDetailLimit,
  });
  const visibleDetailColumns = Number.isFinite(resolvedDetailLimit)
    ? detailColumns.slice(0, resolvedDetailLimit)
    : detailColumns;
  const isDetailedCard = galleryDensity === 'detailed';
  const resolvedBentoColumns = getNormalizedGalleryBentoColumns(galleryBentoColumns, galleryDensity);
  const cardPaddingClassName = getGalleryCardPaddingClassName(galleryDensity);
  const detailGridClassName = getGalleryDetailGridClassName(resolvedBentoColumns);
  const lineDetails = galleryDetailPresentation === 'lines';
  const previewClassName = isImageOnly
    ? 'block h-auto w-full transition-transform duration-500 group-hover:scale-[1.02]'
    : getGalleryPreviewClassName(galleryDensity, isMasonry);
  const customPreview = typeof renderPreview === 'function'
    ? renderPreview({
      row,
      mediaColumn,
      mediaValue,
      isImageMedia,
      isAudioMedia,
      iconVisual,
      IconComponent,
      previewClassName,
    })
    : null;
  const metaContext = { row, contentColumns, detailColumns, titleColumn, summaryColumn, getDisplayText: resolvedGetDisplayText, getFieldValue: resolvedGetFieldValue as any };
  const metaContent = renderMeta?.(metaContext) ?? null;
  const customLeadingVisual = renderLeadingVisual?.({ ...metaContext, iconVisual }) ?? null;
  const defaultLeadingVisual = !customLeadingVisual && iconVisual ? (
    <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg border', iconVisual.chipClassName || 'border-primary/20 bg-primary/10 text-primary')}>
      {renderWorkbenchIcon(iconVisual.Icon || iconVisual.icon, cn('h-4 w-4', iconVisual.iconClassName || 'text-primary'))}
    </div>
  ) : null;
  const leadingVisual = customLeadingVisual || defaultLeadingVisual;
  const defaultMetaClassName = isChromaticBento
    ? 'shrink-0 rounded-full border border-border/40 bg-muted/25 px-2.5 py-1 text-[10px] text-muted-foreground transition-all duration-300 group-hover:border-primary/30 group-hover:bg-primary/10 group-hover:text-primary dark:border-border/35 dark:bg-muted/15'
    : 'shrink-0 rounded-md border border-border/40 bg-muted/25 px-2 py-1 text-[10px] text-muted-foreground dark:border-border/35 dark:bg-muted/15';
  const detailCellClassName = isChromaticBento
    ? 'rounded-xl border border-border/35 bg-muted/20 px-3 py-2.5 transition-all duration-300 group-hover:border-primary/25 group-hover:bg-primary/5 dark:border-border/30 dark:bg-muted/10'
    : 'rounded-lg border border-border/35 bg-muted/20 px-2.5 py-2 dark:border-border/30 dark:bg-muted/10';
  const detailLabelClassName = isChromaticBento
    ? 'text-[10px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-300 group-hover:text-foreground'
    : 'text-[10px] uppercase tracking-[0.12em] text-muted-foreground';
  const detailValueClassName = isChromaticBento
    ? 'mt-1.5 text-[11px] text-muted-foreground transition-colors duration-300 group-hover:text-foreground [&_*]:max-w-full [&_pre]:text-[11px] [&_pre]:leading-relaxed'
    : 'mt-1.5 text-[11px] text-foreground [&_*]:max-w-full [&_pre]:text-[11px] [&_pre]:leading-relaxed';
  const fieldCountLabel = labels?.fieldCount?.(contentColumns.length);
  const coverOnlyHint = labels?.coverOnlyHint;

  const Root = cardInteraction === 'container' ? 'div' : 'button';
  return (
    <Root
      type={cardInteraction === 'button' ? 'button' : undefined}
      onClick={cardInteraction === 'button' ? onClick : undefined}
      aria-label={getLabelText(titleText)}
      className={cn(
        'group relative w-full rounded-lg text-left transition-all duration-300',
        isImageOnly
          ? 'overflow-hidden bg-transparent p-0 shadow-none'
          : isPreviewOnly
            ? 'overflow-hidden bg-transparent p-0'
            : cn('overflow-hidden border', isChromaticBento && 'shadow-none'),
        !isPreviewOnly && !isImageOnly && cardPaddingClassName,
        !isPreviewOnly && !isImageOnly && (isSelected
          ? (isChromaticBento
            ? 'border-primary/35 bg-background/85 ring-1 ring-primary/20'
            : 'border-primary/40 bg-primary/10 ring-1 ring-primary/30')
          : (isChromaticBento
            ? 'workbench-surface-card hover:-translate-y-1 hover:border-primary/20'
            : 'workbench-surface-card hover:border-primary/30')),
        isPreviewOnly && isSelected && 'ring-1 ring-primary/30',
        isImageOnly && (isSelected ? 'ring-1 ring-primary/30' : 'hover:-translate-y-0.5'),
        isMasonry ? 'block h-full w-full min-w-0' : 'h-full min-w-0',
        className,
      )}
    >
      {isChromaticBento ? (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.18),transparent_40%),radial-gradient(circle_at_bottom_left,hsl(var(--accent)/0.18),transparent_45%)] opacity-0 transition-opacity duration-300',
            isSelected ? 'opacity-100' : 'group-hover:opacity-100',
          )}
        />
      ) : null}

      <div className={cn('relative z-[1]', (isImageOnly || isPreviewOnly) && 'rounded-[inherit]')}>
        {customPreview ?? (
          <>
            {isImageMedia && mediaColumn ? (
              <div className={cn(
                'overflow-hidden bg-card/50',
                isImageOnly
                  ? 'rounded-[inherit] bg-transparent'
                  : isPreviewOnly
                    ? 'rounded-[inherit]'
                    : 'mb-3 rounded-lg border border-border/70',
              )}>
                <img
                  src={resolvedGetImagePreviewSrc(mediaValue, (row as any).id, mediaColumn.label)}
                  alt={String(mediaValue || mediaColumn.label || '')}
                  className={previewClassName}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = resolvedGetImagePreviewSrc('', (row as any).id, mediaColumn.label);
                  }}
                />
              </div>
            ) : null}

            {!isImageMedia && iconVisual && isPreviewOnly ? (
              <div className={cn(
                'flex items-center justify-center',
                'rounded-[inherit] border py-10', iconVisual.chipClassName || 'border-primary/20 bg-primary/5',
              )}>
                {renderWorkbenchIcon(iconVisual.Icon || iconVisual.icon, cn('h-10 w-10', iconVisual.iconClassName || 'text-primary'))}
              </div>
            ) : null}

            {isAudioMedia && AudioPlayerComponent ? (
              <div className={cn(!isPreviewOnly && 'mb-3')}>
                <AudioPlayerComponent filename={String(mediaValue)} />
              </div>
            ) : null}
          </>
        )}

        {!isPreviewOnly && !isImageOnly ? (
          <>
            <div className="flex items-start gap-3">
              {leadingVisual ? <div className="shrink-0">{leadingVisual}</div> : null}
              <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className={cn(
                    'flex items-center gap-2 text-sm font-semibold',
                    isChromaticBento ? 'text-muted-foreground transition-colors duration-300 group-hover:text-foreground' : 'text-foreground',
                  )}>
                    <span className="truncate">{cardInteraction === 'container' && onClick ? <button type="button" onClick={onClick} className="border-0 bg-transparent p-0 text-left hover:underline" style={{ font: 'inherit', color: 'inherit' }}>{titleText}</button> : titleText}</span>
                  </div>
                  {summaryText && summaryText !== titleText ? (
                    <div className={cn(
                      'mt-1 text-[11px] leading-5 text-muted-foreground',
                      isDetailedCard ? 'line-clamp-4' : 'line-clamp-2',
                      isChromaticBento && 'transition-colors duration-300 group-hover:text-foreground',
                    )}>
                      {summaryText}
                    </div>
                  ) : null}
                </div>
                {metaContent ?? (hasRenderableNode(fieldCountLabel) ? (
                  <span className={defaultMetaClassName}>
                    {fieldCountLabel}
                  </span>
                ) : null)}
              </div>
            </div>

            {visibleDetailColumns.length > 0 ? (
              <div data-gallery-detail-presentation={galleryDetailPresentation} className={cn('mt-3 grid grid-flow-row gap-2', lineDetails ? 'grid-cols-1' : detailGridClassName)}>
                {visibleDetailColumns.map((column) => (
                  <div key={`${(row as any).id || (row as any).linkKey}-${column.key}`} className={lineDetails ? 'flex min-w-0 items-start gap-2 text-xs' : detailCellClassName}>
                    {column.label ? <div className={lineDetails ? 'shrink-0 text-muted-foreground' : detailLabelClassName}>{formatLabel(column.label, { column })}</div> : null}
                    <div className={lineDetails ? 'min-w-0 text-foreground' : cn(detailValueClassName, !column.label && 'mt-0')}>
                      {resolvedRenderFieldValue(column, row, { compact: galleryDensity !== 'detailed', surface: 'gallery' })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {!summaryText && visibleDetailColumns.length === 0 && hasRenderableNode(coverOnlyHint) ? (
              <div className={cn(
                'mt-3 text-[11px] text-muted-foreground',
                isChromaticBento && 'transition-colors duration-300 group-hover:text-foreground',
              )}>
                {coverOnlyHint}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Root>
  );
}
